/**
 * 优化图片组件
 *
 * 功能：
 * - 响应式图片加载
 * - 懒加载
 * - WebP 格式支持
 * - 渐进式加载
 * - 占位符支持
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  optimizeImageUrl,
  generateSrcSet,
  generateSizes,
  extractImageUrl,
  getPlaceholder,
  isOptimizable,
} from '../../../utils/imageOptimization';
import styles from './OptimizedImage.module.scss';

export const OptimizedImage = ({
  src,
  alt = '',
  width,
  height,
  className = '',
  loading = 'lazy',
  priority = false,
  quality = 80,
  device = 'desktop',
  objectFit = 'cover',
  placeholder = true,
  onLoad,
  onError,
  sitecoreField = null, // Sitecore 图片字段
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [currentSrc, setCurrentSrc] = useState('');

  // 提取图片 URL
  useEffect(() => {
    let imageUrl = '';

    if (sitecoreField) {
      // 从 Sitecore 字段提取
      imageUrl = extractImageUrl(sitecoreField, device);
    } else if (src) {
      imageUrl = src;
    }

    setCurrentSrc(imageUrl);
  }, [src, sitecoreField, device]);

  // 如果没有图片，不渲染
  if (!currentSrc) {
    return null;
  }

  // 优化 URL
  const optimizedSrc = isOptimizable(currentSrc)
    ? optimizeImageUrl(currentSrc, { width, height, quality, device })
    : currentSrc;

  // 生成 srcset
  const srcSet = isOptimizable(currentSrc) ? generateSrcSet(currentSrc, { quality }) : '';

  // 生成 sizes
  const sizes = srcSet ? generateSizes('default') : '';

  // 占位符
  const placeholderSrc = placeholder && isOptimizable(currentSrc) ? getPlaceholder(currentSrc) : '';

  // 处理加载完成
  const handleLoad = (event) => {
    setIsLoaded(true);
    setHasError(false);
    if (onLoad) onLoad(event);
  };

  // 处理加载错误
  const handleError = (event) => {
    setHasError(true);
    console.warn(`Failed to load image: ${currentSrc}`);
    if (onError) onError(event);
  };

  // 优先级加载
  const loadingProp = priority ? 'eager' : loading;
  const fetchPriority = priority ? 'high' : 'auto';

  return (
    <div
      className={`${styles.container} ${className}`}
      style={{
        width: width ? `${width}px` : '100%',
        height: height ? `${height}px` : 'auto',
      }}
    >
      {/* 占位符 */}
      {placeholder && placeholderSrc && !isLoaded && !hasError && (
        <img
          src={placeholderSrc}
          alt=""
          aria-hidden="true"
          className={styles.placeholder}
          style={{ objectFit }}
        />
      )}

      {/* 主图片 */}
      <picture>
        {/* WebP 格式 */}
        {srcSet && <source type="image/webp" srcSet={srcSet} sizes={sizes} />}

        {/* 原始格式 */}
        <img
          src={optimizedSrc}
          srcSet={srcSet || undefined}
          sizes={sizes || undefined}
          alt={alt}
          width={width}
          height={height}
          loading={loadingProp}
          decoding={priority ? 'sync' : 'async'}
          fetchPriority={fetchPriority}
          onLoad={handleLoad}
          onError={handleError}
          className={`${styles.image} ${isLoaded ? styles.loaded : styles.loading} ${
            hasError ? styles.error : ''
          }`}
          style={{ objectFit }}
        />
      </picture>

      {/* 错误占位符 */}
      {hasError && (
        <div className={styles.errorPlaceholder}>
          <span></span>
        </div>
      )}
    </div>
  );
};

OptimizedImage.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
  className: PropTypes.string,
  loading: PropTypes.oneOf(['lazy', 'eager']),
  priority: PropTypes.bool,
  quality: PropTypes.number,
  device: PropTypes.oneOf(['mobile', 'tablet', 'desktop', 'hero']),
  objectFit: PropTypes.oneOf(['cover', 'contain', 'fill', 'none', 'scale-down']),
  placeholder: PropTypes.bool,
  onLoad: PropTypes.func,
  onError: PropTypes.func,
  sitecoreField: PropTypes.object,
};

export default OptimizedImage;
